'use client';
import React, { forwardRef, useContext, useEffect, useRef } from 'react';
import * as S from './metric-items.styles';
import { HeartFilled } from './HeartFilledIcon';
import { Heart } from './HeartIcon';
import { ø } from '../types';
import { LotSettingsContext } from '../_Providers/LotProvider';
import { UIText, GavelIcon } from '@/component-library';
import { useEffectOnce } from 'react-use'
import { StaticLotData } from '../schema.design';

export const MetricItems = ({
  views,
  watching,
  setIsWatching,
  isWatching,
  label = {
    views: 'views',
    watching: 'watching',
  },
  enableSpecs = false,
  lotData,
}) => (
  <S.ButtonGroup>
    {enableSpecs && lotData && <UsersSpecs lotData={lotData} />}
    <S.Button onClick={() => void {}}>
      <ThrobbingMetricItem value={32} label="bids" Icon={GavelIcon} />
    </S.Button>
    <S.Button>
      <ThrobbingMetricItem value={views} label={label.views} Icon={S.Wink} />
    </S.Button>
    <S.Button onClick={() => setIsWatching((p) => !p)}>
      <ThrobbingMetricItem
        key={String(isWatching)}
        value={watching}
        label={label.watching}
        Icon={isWatching ? HeartFilled : Heart}
      />
    </S.Button>
  </S.ButtonGroup>
);

export const UsersSpecs = ({ lotData }) => {
  lotData as StaticLotData;
  let [userSettings, { removeUserSpec }] = useContext(LotSettingsContext),
    specs;
  useEffectOnce(() => {});
  if (!userSettings?.specs) {
    return null;
  }
  specs = { ...lotData.properties };

  return (
    <>
      {Object.entries(userSettings.specs)
        .filter(([id]) => id)
        .reverse()
        .map(
          ([id, { title, src }], i: ø) => (
            (i = i + id),
            (
              <S.UsersSpecs
                key={i + id}
                title={title}
                onClick={() => removeUserSpec(id)}
              >
                <img width={24} src={src} id={id} />
                {specs[id] && (
                  <UIText title={`${title} ${specs[id]}`} as="h5">
                    {specs[id]}
                  </UIText>
                )}
              </S.UsersSpecs>
            )
          )
        )}
    </>
  );
};

export function g_userSettings() {
  if (typeof window === 'undefined') {
    return { spec: {} };
  }
  return JSON.parse(localStorage.getItem('userSettings') || '{}');
}

const MetricItem = forwardRef<
  HTMLLIElement,
  {
    Icon: React.ReactElement;
    label: string;
    value: number;
    displayValue?: boolean;
  }
>(
  ({ Icon, label, value, displayValue = true }, ref) => (
    //@ts-ignore
    (Icon = React.cloneElement(Icon, { width: 24 })),
    (
      <S.Li ref={ref}>
        {Icon}
        <UIText as="h5">
          {displayValue && <data value={value}>{value}</data>}
          {label && <span>{` ${label}`}</span>}
        </UIText>
      </S.Li>
    )
  )
);

export const useBeginAnimationOnChange = (
  ref: React.RefObject<HTMLElement | SVGElement | null>,
  value: string | number
) => {
  function animate() {
    if (!ref.current) return;

    const animationEls = ref.current.querySelectorAll(
      'animateTransform[begin="indefinite"], animate[begin="indefinite"]'
    ) as NodeListOf<SVGAnimateElement | SVGAnimateTransformElement>;

    animationEls.forEach((el) => {
      el.beginElement();
    });
  }
  const isMounted = useRef(false)
  useEffect(() => {
    if (isMounted.current) {
      animate()
    }
    isMounted.current = true
  }, [value])
};

export const ThrobbingMetricItem: React.FC<{
  value: number;
  label: string;
  Icon: React.ElementType;
  displayValue?: boolean;
}> = ({ value, label, Icon, displayValue = true }) => {
  const liRef = useRef<HTMLLIElement>(null);

  useBeginAnimationOnChange(liRef, value);

  return (
    <MetricItem
      ref={liRef}
      Icon={<Icon />}
      label={label}
      value={value}
      displayValue={displayValue}
    />
  );
};
